import React from "react";
import { toast } from "sonner";
import { Check, EyeOff, Inbox, X } from "lucide-react";

import { ClientFactory } from "@/main/common/ClientFactory";
import { GroupGovernanceApi } from "@/main/common/GroupGovernanceApi";
import type { GroupInviteRequestRow } from "@/main/common/GroupInviteRequestTypes";
import { getSafeToastMessageFromError, logApiFailure } from "@/main/common/ApiUtils";
import { getDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { localizeWithMemberNames } from "@/main/react/component/localizeWithMemberName";
import { MAIN_PAGE_LIST_WIDTH_CLASS, MainPageListStack } from "@/main/react/list/MainPageListLayout";
import { PaginatedListEmptyState, PaginatedListRetryState } from "@/main/react/list/PaginatedListState";
import { ProfileCardListSkeleton } from "@/main/react/list/PaginatedListSkeletons";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import type { Localizer } from "@/main/react/Types";
import { showIgnoredUndoToast } from "@/main/react/Dialog/ignoredUndoToast";

/**
 * The leader queue for member invite requests (the fifth manage-console queue).
 *
 * <p>Design: `designs/01-now/2026-09-02-member-invite-request-with-leader-preapproval-design.md` §8.
 *
 * <p><b>Deliberately NOT built on `PaginatedListWrapperV2`</b>, which every neighbouring section uses.
 * That wrapper is cursor-based (`fetchPage(cutoff)`) and this endpoint is a Spring `Page`, so adopting
 * it would mean either inventing a cursor the server does not have or faking one from `createdAt` —
 * and a fake cursor silently drops rows that share a timestamp. The queue is bounded by construction
 * anyway: `GroupInviteRequestService` caps pending requests at 5 per member per group, so a page is
 * the realistic whole of it. ⚠️ If that ceiling is raised, this needs real pagination rather than a
 * bigger page.
 *
 * <p>⚠️ Approving here is NOT the same as approving a join request. It writes a real
 * `group_invitations` row attributed to the REQUESTING MEMBER (owner decision D2), so the person named
 * receives an ordinary invitation from someone they recognise, and the nominee learns nothing at all
 * if this is declined instead.
 */
export function GroupInviteRequestsSection({
    groupPublicId,
    localizer,
    onResolved,
    onRestored,
}: {
    groupPublicId: string;
    localizer: Localizer;
    /** Lets the page decrement its queue badge without refetching the bootstrap. */
    onResolved?: () => void;
    /** SV-146: an undone Ignore puts the row back, so the page re-increments the badge. */
    onRestored?: () => void;
}) {
    const [rows, setRows] = React.useState<GroupInviteRequestRow[] | null>(null);
    const [failed, setFailed] = React.useState(false);
    const [busyId, setBusyId] = React.useState<string | null>(null);

    const load = React.useCallback(async () => {
        try {
            setFailed(false);
            const page = await GroupGovernanceApi.listInviteRequests(groupPublicId);
            setRows(page);
        } catch (error) {
            logApiFailure("[GroupPage] fetch invite requests error", error);
            // Never render an empty state over a failed fetch: "nobody has asked" and "we could not
            // find out" are different facts, and only one of them is safe to act on (rule 32).
            setFailed(true);
            setRows(null);
        }
    }, [groupPublicId]);

    React.useEffect(() => {
        void load();
    }, [load]);

    const resolve = React.useCallback(
        async (row: GroupInviteRequestRow, action: "approve" | "decline") => {
            const publicId = row.publicId;
            if (!publicId) return;
            setBusyId(publicId);
            try {
                if (action === "approve") {
                    await GroupGovernanceApi.approveInviteRequest(publicId);
                    toast.success(localizer("group.invite_request.approved"));
                } else {
                    await GroupGovernanceApi.declineInviteRequest(publicId);
                    toast.success(localizer("group.invite_request.declined"));
                }
                // Drop the row locally rather than refetching: the server has already resolved it, and
                // a refetch would race a sibling leader acting on the same queue.
                setRows((current) => (current ?? []).filter((item) => item.publicId !== publicId));
                onResolved?.();
            } catch (error) {
                logApiFailure("[GroupPage] resolve invite request error", error);
                toast.error(getSafeToastMessageFromError(error, localizer("paginated.load_failed")));
                // Reload, because the most likely cause is that another leader answered it first and
                // this row no longer exists.
                void load();
            } finally {
                setBusyId(null);
            }
        },
        [localizer, load, onResolved],
    );

    /**
     * SV-146 (D7): Ignore is one click with no dialog. The requester is never told (the row stays
     * PENDING for them), the row leaves this queue at once, and the toast's Undo puts it back where it was.
     */
    const ignore = React.useCallback(
        async (row: GroupInviteRequestRow) => {
            const publicId = row.publicId;
            if (!publicId) return;
            setBusyId(publicId);
            try {
                await ClientFactory.getAjaxGroupRestControllerApi().ignoreInviteRequest(publicId);
                setRows((current) => (current ?? []).filter((item) => item.publicId !== publicId));
                onResolved?.();
                showIgnoredUndoToast({
                    localizer,
                    message: "groups.request.ignore.success",
                    undo: () => ClientFactory.getAjaxGroupRestControllerApi().undoIgnoreInviteRequest(publicId),
                    onUndone: () => {
                        setRows((current) => ((current ?? []).some((item) => item.publicId === publicId)
                            ? current
                            : [...(current ?? []), row]));
                        onRestored?.();
                    },
                });
            } catch (error) {
                logApiFailure("[GroupPage] ignore invite request error", error);
                toast.error(getSafeToastMessageFromError(error, localizer("groups.request.ignore.failed")));
                void load();
            } finally {
                setBusyId(null);
            }
        },
        [localizer, load, onResolved, onRestored],
    );

    if (rows === null && !failed) {
        return <ProfileCardListSkeleton />;
    }

    if (failed) {
        // A failed fetch is NOT an empty queue. `PaginatedListEmptyState` defaults `variant` to
        // "empty", so the previous version of this branch emitted `data-list-state="empty"` with the
        // same icon and title as the genuinely-empty branch below — the two states were
        // indistinguishable to a member AND to a browser spec, and a leader read "nobody has asked"
        // when the truth was "we could not find out". `PaginatedListRetryState` is what the
        // neighbouring group sections use: it emits `data-list-state="failed"` and, unlike an empty
        // state, gives the member something to press. Both keys are already translated 25/25.
        return (
            <div className={MAIN_PAGE_LIST_WIDTH_CLASS}>
                <PaginatedListRetryState
                    message={localizer("paginated.load_failed")}
                    retryLabel={localizer("literal.retry")}
                    onRetry={load}
                />
            </div>
        );
    }

    if (!rows || rows.length === 0) {
        return (
            <div className={MAIN_PAGE_LIST_WIDTH_CLASS}>
                <PaginatedListEmptyState
                    icon={Inbox}
                    titleHeadingLevel={2}
                    title={localizer("group.section.invite_requests")}
                    description={localizer("paginated.empty.description")}
                />
            </div>
        );
    }

    return (
        <div className={MAIN_PAGE_LIST_WIDTH_CLASS} data-playwright-id="group-invite-requests-section">
            <MainPageListStack>
                {rows.map((row) => (
                    <div
                        key={row.publicId ?? undefined}
                        data-playwright-id="group-invite-request-row"
                        className="flex flex-col gap-3 rounded-2xl border border-border bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                        <p className="text-sm leading-5 text-neutral-text">
                            {/*
                              * Display names, not raw handles, and not `?? ""` (register F22). The
                              * empty-string fallback rendered the sentence with a HOLE in it where a
                              * person's name belongs — "invited  to join" — which is what a member sees
                              * when either side of an invite has since been deleted.
                              * `getDisplayUsername` normalizes and falls back to the existing
                              * `literal.deleted_user` copy, which is the answer every other surface in
                              * the product already gives.
                              */}
                            {localizeWithMemberNames(localizer, "group.invite_request.row_summary", [
                                getDisplayUsername(row.requestedByUsername, localizer),
                                getDisplayUsername(row.invitedUsername, localizer),
                            ])}
                        </p>
                        <div className="flex shrink-0 gap-2">
                            {/* SV-146: primary-last row, so the order is the mirror of Approve · Decline · Ignore. */}
                            <CtaButton
                                icon={EyeOff}
                                text={localizer("groups.request.ignore")}
                                variant="ghost"
                                size="sm"
                                disabled={busyId === row.publicId}
                                playwrightId="group-invite-request-ignore"
                                onClick={() => void ignore(row)}
                            />
                            <CtaButton
                                icon={X}
                                text={localizer("groups.request.reject")}
                                variant="outline"
                                size="sm"
                                disabled={busyId === row.publicId}
                                playwrightId="group-invite-request-decline"
                                onClick={() => void resolve(row, "decline")}
                            />
                            <CtaButton
                                icon={Check}
                                text={localizer("groups.request.approve")}
                                size="sm"
                                disabled={busyId === row.publicId}
                                playwrightId="group-invite-request-approve"
                                onClick={() => void resolve(row, "approve")}
                            />
                        </div>
                    </div>
                ))}
            </MainPageListStack>
        </div>
    );
}
